import { useEffect, useState } from 'react';
import api from '../api/axios';
import './LeadAssignmentUpload.css';

function LeadAssignmentUpload({ onUploadSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState([]);
  const [salesEmployees, setSalesEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  useEffect(() => {
    fetchSalesEmployees();
  }, []);

  const fetchSalesEmployees = async () => {
    try {
      console.log('Fetching sales employees...');
      const response = await api.get('/employees');
      console.log('All employees response:', response.data);
      // Filter only employees with Sales role
      const salesEmps = response.data.filter(emp => emp.role?.name === 'Sales');
      console.log('Filtered sales employees:', salesEmps);
      setSalesEmployees(salesEmps);
    } catch (error) {
      console.error('Error fetching sales employees:', error.response?.data || error.message);
      setSalesEmployees([]);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if it's an Excel file
      const allowedExtensions = ['xlsx', 'xls'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (!allowedExtensions.includes(fileExtension)) {
        setMessage('');
        setErrors(['Only Excel files (.xlsx, .xls) are allowed']);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setErrors([]);
      setMessage('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrors(['Please select a file']);
      return;
    }

    if (!selectedEmployee) {
      setErrors(['Please select a sales employee']);
      return;
    }

    setLoading(true);
    setMessage('');
    setErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('employeeId', selectedEmployee);

      console.log('📤 Uploading file:', selectedFile.name);
      console.log('📦 Employee ID:', selectedEmployee);

      const response = await api.post('/lead-assignments/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('✅ Upload successful:', response.data);
      setMessage(`✅ ${response.data.message}`);
      setSelectedFile(null);
      setSelectedEmployee('');
      
      if (response.data.errors && response.data.errors.length > 0) {
        setErrors(response.data.errors);
      }

      // Reset file input
      document.getElementById('file-input').value = '';

      // Call callback after success
      if (onUploadSuccess) {
        setTimeout(onUploadSuccess, 1000);
      }
    } catch (error) {
      console.error('❌ Upload failed:', error);
      console.error('📋 Error response:', error.response?.data);
      
      setErrors([error.response?.data?.message || 'Error uploading file']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lead-assignment-upload">
      {/* Sales Employees Section */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '15px' }}>👥 Sales Team Members</h3>
        {salesEmployees.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
            {salesEmployees.map((employee) => (
              <div key={employee._id} style={{ 
                padding: '15px', 
                border: '1px solid #ddd', 
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>
                  {employee.firstName} {employee.lastName}
                </h4>
                <p style={{ margin: '5px 0', fontSize: '13px', color: '#666' }}>
                  <strong>Email:</strong> {employee.email}
                </p>
                <p style={{ margin: '5px 0', fontSize: '13px', color: '#666' }}>
                  <strong>ID:</strong> {employee.employeeId}
                </p>
                <p style={{ margin: '5px 0', fontSize: '13px', color: '#666' }}>
                  <strong>Department:</strong> {employee.department || 'N/A'}
                </p>
                <span style={{ 
                  display: 'inline-block',
                  padding: '4px 8px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '12px',
                  marginTop: '8px'
                }}>
                  {employee.role?.name || 'Sales'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No sales employees found</p>
        )}
      </div>

      <hr style={{ margin: '30px 0' }} />

      <div className="upload-section">
        <h3 style={{ marginBottom: '15px' }}>📤 Upload Leads (Excel Format)</h3>
        
        {/* Select Sales Employee */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
            Select Sales Employee *
          </label>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            disabled={loading}
            style={{ 
              width: '100%', 
              padding: '10px', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="">-- Choose a Sales Employee --</option>
            {salesEmployees.map((employee) => (
              <option key={employee._id} value={employee._id}>
                {employee.firstName} {employee.lastName} ({employee.employeeId})
              </option>
            ))}
          </select>
        </div>
        
        {/* File Input */}
        <div className="file-input-wrapper">
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={loading}
            style={{ display: 'none' }}
          />
          <label htmlFor="file-input" className="file-label">
            <span>📁 Click to select Excel file</span>
            {selectedFile && <span className="file-name">({selectedFile.name})</span>}
          </label>
        </div>

        {/* Template Info */}
        <div className="template-info">
          <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            <strong>ℹ️ Excel Format:</strong>
          </p>
          <ul style={{ fontSize: '12px', color: '#666', margin: '8px 0', paddingLeft: '20px' }}>
            <li>✅ Any Excel format is accepted (.xlsx, .xls)</li>
            <li>✅ First column will be treated as Lead Name</li>
            <li>✅ Second column will be treated as Contact Number (if available)</li>
            <li>✅ Third column will be treated as Email (if available)</li>
            <li>✅ Fourth column will be treated as Company Name (if available)</li>
            <li>⚠️ Just upload your Excel file - we'll handle the rest!</li>
          </ul>
        </div>

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={!selectedFile || !selectedEmployee || loading}
          className="btn btn-primary"
          style={{ width: '100%', marginTop: '15px', padding: '10px', opacity: (!selectedFile || !selectedEmployee || loading) ? 0.6 : 1 }}
        >
          {loading ? '⏳ Uploading...' : '📤 Upload Leads'}
        </button>

        {/* Success Message */}
        {message && (
          <div style={{
            marginTop: '15px',
            padding: '12px',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            border: '1px solid #c3e6cb'
          }}>
            {message}
          </div>
        )}

        {/* Error Messages */}
        {errors.length > 0 && (
          <div style={{
            marginTop: '15px',
            padding: '12px',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            border: '1px solid #f5c6cb'
          }}>
            <strong>⚠️ Errors:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '13px' }}>
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeadAssignmentUpload;
